import { inject, Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subscription } from 'rxjs';
import { AuthService } from './auth.service'; // anpassen
import { UserProfile, UserProfileData } from '../classes/UserProfile'; // anpassen auf deinen Klassennamen
import { UserProfileService } from './user-profile.service';
import { Firestore, doc, getDoc, onSnapshot, setDoc } from '@angular/fire/firestore';
import { User } from '@angular/fire/auth';
import { AlertController } from '@ionic/angular';
import { ToastService } from './toast.service';
import { AnalyticsService } from './analytics.service';

@Injectable({
  providedIn: 'root',
})
export class ProfileService implements OnDestroy {
  private fireDb = inject(Firestore);
  private authService = inject(AuthService);
  private analyticsService = inject(AnalyticsService);
  private readonly collectionPath = 'userProfile';

  userProfileService = inject(UserProfileService);

  private userProfileRef: ReturnType<typeof doc> | null = null;
  private profileSub: (() => void) | null = null; // onSnapshot unsubscribe

  private profileSubject = new BehaviorSubject<UserProfile | null>(null);

  /**
   * Usage:
   * Als Stream
   * profile$ = this.profileService.profile$;
   * <div *ngIf="profile$ | async as profile">
  <p>Hallo, {{ profile.displayName || 'Gast' }}</p>
  <p>Credits: {{ profile.credits.available }}</p>
  </div>

  Als direkter Zugriff:
  const profile = this.profileService.userProfile;
  */
 public readonly profile$ = this.profileSubject.asObservable();

  get userProfile(): UserProfile | null {
    return this.profileSubject.value;
  }

  /**
   * Loest die Rolle einmalig auf.
   *
   * Bewusst nicht ueber profile$: das ist ein BehaviorSubject mit Startwert
   * null, dort laesst sich "noch nicht geladen" nicht von "kein Profil"
   * unterscheiden. Wer auf die erste Emission wartet, bekaeme also auch fuer
   * eingeloggte Admins moeglicherweise false.
   *
   * Gleiches Muster wie AdminGuard – die Rolle steht im
   * userProfile-Dokument, das die Rule nur fuer den Besitzer lesbar macht.
   */
  async isAdmin(): Promise<boolean> {
    const cached = this.userProfile;
    if (cached) return cached.role === 'admin';

    const user = await this.authService.getUser();
    if (!user) return false;

    try {
      const snap = await getDoc(
        doc(this.fireDb, `${this.collectionPath}/${user.uid}`)
      );
      return snap.exists() && (snap.data() as UserProfileData).role === 'admin';
    } catch (err) {
      console.error('isAdmin: Rolle nicht lesbar', err);
      return false;
    }
  }

  // get userProfile(): UserProfile {
  //   const profile = this.profileSubject.value;
  //   if (!profile) {
  //     throw new Error('UserProfile is required but not available');
  //   }
  //   return profile;
  // }

  private authSub?: Subscription;

  constructor(
    private alertController: AlertController
  ) {
    // beim Konstruktor direkt auf Auth-State hören
    this.authSub = this.authService.authState$.subscribe((fbUser) => {
      if (fbUser) {
        this.attachToUserProfile(fbUser.uid);
      } else {
        this.detachProfileListener();
        this.profileSubject.next(null);
      }
    });
  }
  ngOnDestroy(): void {
    this.detachProfileListener();
    this.authSub?.unsubscribe();
  }

  private attachToUserProfile(uid: string) {
    // alten Listener aufräumen
    this.detachProfileListener();

    this.userProfileRef = doc(this.fireDb, `${this.collectionPath}/${uid}`);

    this.profileSub = onSnapshot(
      this.userProfileRef,
      async (snap) => {
        if (!snap.exists()) {
          // optional: Default-Profil anlegen oder ggf direkt createUser()
          const us = UserProfile.init(uid);
          // await setDoc(this.userProfileRef!, us.getDataObject());
          this.profileSubject.next(us);
          return;
        }

        try {
          const data = snap.data() as UserProfileData;
          const profile = UserProfile.initFromObject(data);
          this.profileSubject.next(profile);
        } catch (e) {
          console.error(
            'Invalid UserProfileData from Firestore',
            e,
            snap.data()
          );
          this.profileSubject.next(null);
        }
      },
      (error) => {
        console.error('Error listening to userProfile', error);
        this.profileSubject.next(null);
      }
    );
  }

  private detachProfileListener() {
    if (this.profileSub) {
      this.profileSub();
      this.profileSub = null;
    }
    this.userProfileRef = null;
  }

  /**
   * Profil speichern (z. B. nach Update im Profil-Formular).
   */
  async saveProfile(profile: UserProfile | null = this.userProfile): Promise<boolean> {
    if (!this.userProfileRef) {
      console.warn('saveProfile called but no userProfileRef set');
      return false;
    }
    if (!profile) {
      console.warn('saveProfile called but profile is invalid');
      return false;
    }

    try {
      const data = profile.getDataObject();
      await setDoc(this.userProfileRef, data, { merge: true });
      return true;
    } catch (error) {
      console.error('Error saving user profile', error);
      return false;
    }
  }

  async askForDisplayName() {
    const alert = await this.alertController.create({
      cssClass: "keyboard-alert text-align-left",
      header: "Profilname",
      message: "Gebe deinen Profilnamen ein um fortzufahren",
      inputs: [{ name: "name", placeholder: "Dein Name" }],
      buttons: [
        { text: "Abbrechen", role: "cancel" },
        {
          text: "Fortfahren",
          handler: async (data) => {
            const name = (data?.name ?? "").trim();
            if (!name) {
              ToastService.makeLowToast("Bitte gib einen Namen ein.");
              return false; // verhindert, dass der Alert schließt
            }
            this.userProfile?.setDisplayName(name);
            await this.saveProfile();
            return true;
          },
        },
      ],
    });

    await alert.present();
  }

async ensureProfile(user: User): Promise<UserProfile | null> {
  if (!user) return null;

  const providerName = user.providerData?.find(p => p.displayName)?.displayName;
  const providerMail = user.providerData?.find(p => p.email)?.email;

  const name = user.displayName ?? providerName ?? '';
  const email = user.email ?? providerMail ?? '';

  // IMPORTANT: Prüfung "existiert schon?"
  this.userProfileRef = doc(this.fireDb, `${this.collectionPath}/${user.uid}`);
  const userDoc = await getDoc(this.userProfileRef);
  if(userDoc.exists()) {
    return UserProfile.initFromObject(userDoc.data() as UserProfileData);
  }

  // Anmeldeweg fuer die Auswertung: 'password', 'google.com', 'apple.com', ...
  const method = user.providerData?.[0]?.providerId
    ?? (user.isAnonymous ? 'anonymous' : 'unknown');

  return this.createUser(user.uid, name, email, method);
}

  /**
   * Erstellen eines neuen Profils.
   */
  async createUser(userid: string, name: string, email: string = '', method: string = 'unknown'): Promise<UserProfile | null> {
    this.userProfileRef = doc(this.fireDb, `${this.collectionPath}/${userid}`);

    try {
      let us: UserProfile;
      if (email) {
        us = UserProfile.init(userid, name, email);
      } else {
        // AnonUser
        us = UserProfile.init(userid, name);
      }
      await setDoc(this.userProfileRef, us.getDataObject());

      // Nur echte Konten zaehlen als Signup. Anonyme Profile sind keins - deren
      // Umwandlung wird in convertAnonUser erfasst, sonst waere die Kennzahl
      // ab dem Anonymous-First-Umbau nicht mehr vergleichbar.
      if (email) {
        void this.analyticsService.trackOnce(userid, 'signup', {
          userId: userid,
          method,
        });
      }

      return us;

    } catch (error) {
      console.error('Error creating user profile', error);
      return null;
    }
  }

  async convertAnonUser(
    name: string,
    email: string,
    password: string
  ): Promise<string> {
    const errorCode = await this.authService.convertAnonUserToPermanent(
      email,
      password
    );

    // Beim Zusammenfuehren mit einem bestehenden Konto darf das Profil NICHT
    // ueberschrieben werden: dort stehen Name und Adresse des echten Kontos,
    // und `userProfile` zeigt in diesem Moment womoeglich noch auf das anonyme.
    if (errorCode === AuthService.CONVERT_MERGED) {
      return "";
    }

    if (!errorCode) {
      let us = this.userProfile;
      if (us) {
        us.convertAnonUser(name, email);
        await this.saveProfile(us);
      }

      // Aus einem anonymen wird ein echtes Konto - das ist der Signup.
      const uid = this.authService.userId;
      if (uid) {
        void this.analyticsService.trackOnce(uid, 'signup', {
          userId: uid,
          method: 'anon_conversion',
        });
      }
    }

    return errorCode;
  }
}


